import { createAsyncThunk } from '@reduxjs/toolkit';
import api from 'api';
import { TFunction } from 'i18next';
import { IDataRoom, IGroup, IPendingInviteUsers, IUsers } from 'interfaces';
import { RootState } from 'store';
import dayjs from 'dayjs';
import { setDataRoom } from 'store/slices/dataRoomSlice';
import { setRooms } from 'store/slices/userDataSlice';
import { setDeletedInviteId, setIsConfirmBlockOpen, setIsConfirmDeleteOpen, setPendingInviteUsers, setProcessedUsers, setUserAndData, setUserChoosen } from 'store/slices/dataUsersSlice';

export const defaultGroups = ['full_administrator', 'user'];

const fetchGroupsAndUsers = createAsyncThunk('dataUsers/fetchAll', async (payload: {room_id: string}, thunkApi) => {  
  try {    
    const usersRespone = await api.getUsers(payload.room_id);
    const groupsResponce = await api.getGroups(payload.room_id);
    const pendingInviteUsers = (await api.getPendingInviteUsers(payload.room_id)).data.filter(
      ({ invite_status, parent_invite_id }) => invite_status !== 1 || parent_invite_id !== null
    );

    const rootState = thunkApi.getState() as RootState;
    const { dataRoom } = rootState.dataRoom;
    const { available_rooms } = rootState.userData;
    const { dispatch } = thunkApi;

    const users = usersRespone.data.map((user) => {
      const inSeconds = dayjs(user.entered_at).valueOf();
      return {
        ...user,
        key: user.id,
        entered_at: user.entered_at ? inSeconds : 0,
        emailOrRole: user.email,
        type: 'user',
        isOwner: dataRoom?.owner_id === user.id,
      }
    });

    const currentRoom = available_rooms.find(room => room.id === dataRoom?.id);    

    const roomWithFreshData = {
      ...currentRoom,
      user_status: dataRoom?.user_status,
      watermark_enabled: dataRoom?.watermark_enabled,
      available_users: dataRoom?.available_users,
      agreement_file: dataRoom?.agreement_file,
      agreement_id: dataRoom?.agreement_id,
      user_status_change_at: dataRoom?.user_status_change_at,
      rooms_constraints: {
        ...currentRoom?.rooms_constraints,
        members_amount: users.length,
      }
    } as IDataRoom;

    dispatch(setRooms(available_rooms.map((someRoom) =>
      someRoom.id === dataRoom?.id
        ? roomWithFreshData
        : someRoom
    )));

    dispatch(setDataRoom(roomWithFreshData));

    const groups = groupsResponce.data.map((el) => ({
      ...el,
      id: el.group_id!,
      name: el.group_name!,
      key: el.group_id,
      first_name: el.group_name,
      emailOrRole: el.role?.name,
      type: 'group'
    }));

    const dataUserTable = groups.map((group) => {
      const usersOfThisGroup = users.filter((user: IUsers) => user.group.includes(group?.id));
      return {
        ...group,
        first_name: `${group?.first_name} (${usersOfThisGroup.length})`,
        children: usersOfThisGroup.map((user: IUsers) => ({...user, key: `${user.key} ${group.key}`})),
      }
    });

    return {
      users,
      groups,
      pendingInviteUsers,
      dataUserTable: [...dataUserTable, ...users.filter((user:IUsers) => user.group.length === 0)]
    };
  } catch (Err) {
    return thunkApi.rejectWithValue('Failed to download Groups/Users');
  }
});

export const fetchOnlyGroups = createAsyncThunk('dataUsers/fetchGroups', async (payload: string, thunkApi) => {
  try {
    const groups = await api.getGroups(payload);
    const preparedGroups: IGroup[] = groups.data.map((el) => ({
      ...el,
      id: el.group_id!,
      name: el.group_name!,
      key: el.group_id,
      first_name: el.group_name,
      emailOrRole: el.role?.name,
      type: 'group'
    }));
    
    return preparedGroups;
  } catch (Err) {
    return thunkApi.rejectWithValue('Failed to download Groups/Users');
  }
});

export const fetchUserLogs = createAsyncThunk('dataUsers/fetchUserlogs', async (props: {user_id: string, room_id: string, locale: string}, thunkApi) => {
  try {
    const localization = props.locale === 'es' ? 'en' : props.locale;
    const logs = await api.getUserLogs(props.user_id, props.room_id, localization);    
    return {
      userLogs: logs.data
    }
  } catch (e) {
    return thunkApi.rejectWithValue("Failed to download user's logs");
  }
});

export const fetchPermissions = createAsyncThunk('dataUsers/fetchPermissions', async (props: {t: TFunction}, thunkApi) => {
  const arrWithCorrectOrder = [
    'can_create_access_terms', 'can_create_access_point', 'can_edit_room_settings', 'can_view_logs',
    'can_delete_room', 'can_invite_users', 'can_exclude_users', 'can_create_group_all_roles', 'can_edit_roles',
    'can_create_folder', 'can_edit_folder_permissions', 'can_edit_agreement', 'can_manage_signing',
    'can_make_screenshot',
  ];  
  try {
    const permissions = await api.getPermissions('room');    
    return permissions.data
      .sort((a: any,b:any) => arrWithCorrectOrder.findIndex(el => el === a.type) - arrWithCorrectOrder.findIndex(el => el === b.type))
      .map(permission => ({
        ...permission,
        name: props.t(`Users.modals.permissions.titles.${permission.type}`),
        description: props.t(`Users.modals.permissions.descriptions.${permission.type}`)
      }));
  } catch (e) {
    return thunkApi.rejectWithValue("Failed fetching permissions");
  }
});

export const setNewRole = createAsyncThunk('setNewRole/Roles',
  async (props: {user: IUsers, group_id: string, room_id: string, details: any, t: TFunction}, thunkApi) => {
  try {
    await api.addUserToGroup({
      email: props.user.email,
      group_id: props.group_id,
      room_id: props.room_id,
      details: props.details,
    })
    return {user: props.user, group_id: props.group_id, t: props.t}
  } catch (e) {
    return thunkApi.rejectWithValue("Failed setting new role");
  }
});

export const usersTableWithNewUsersRole = (
  data: any,
  users: IUsers[],
  currentUser: IUsers,
  group_id: String,
  t: TFunction,
) => {
  const resultdata = data.map((elem: any) => {
    const targetUser = users.find((user) => user.email === currentUser?.email);
    const newSelectedUser = { ...currentUser!, group: [group_id] };
    const nameOfGroup = defaultGroups.includes(elem?.name)
      ? t(`Users.table.${elem?.name}`)
      : elem?.name;

    return elem.id === group_id
      ? {
          ...elem,
          first_name: ` ${nameOfGroup} (${elem.children.length + 1})`,
          children: [
            ...elem.children,
            { 
              ...targetUser,
              group: newSelectedUser?.group,
              key: `${targetUser?.key} ${group_id}`
            },
          ],
        }
      : (
        elem.id === targetUser?.group[0]
        ? {
          ...elem,
          first_name: ` ${nameOfGroup} (${elem.children.length - 1})`,
          children: elem.children.filter((user: any) => user.email !== targetUser!.email)
        }
        : elem
      )
  })
  .filter((elem: any) => elem.email !== currentUser?.email);      
  return resultdata
}

export const fetchGlobalUsers = createAsyncThunk('fetch/globalUsers', async (payload, thunkApi) => {
  try{
    const resposnse = await api.getGlobalUsers();
    return resposnse.data
  }catch(err){
    return thunkApi.rejectWithValue('Failed fetching users')
  }
})

export const fetchUsersOnSokcet = createAsyncThunk(
  'fetch/usersFromSocket',
  async (payload: { room_id: string }, thunkApi) => {
    try {
      const { dataRoom } = (thunkApi.getState() as RootState).dataRoom;
      const { dispatch } = thunkApi;

      if(dataRoom?.id === payload.room_id){
        dispatch(fetchGroupsAndUsers({ room_id: payload.room_id }));
      }
    } catch (err) {
      console.log(err);
    }
  }
);

export const deleteUserFromStates = createAsyncThunk('cleanFromUser/Users',
  async (props: { selectedUser: IUsers }, thunkApi) => {
  const { dataUserTable, users } = (thunkApi.getState() as RootState).dataUsers;
  const { dataRoom } = (thunkApi.getState() as RootState).dataRoom;
  const { available_rooms } = (thunkApi.getState() as RootState).userData;

  const { dispatch } = thunkApi;

  const tableDataWithoutSelectedUser = dataUserTable.map((elem) => {
    let first_name: string;
    const isNeedToChangeName = elem.children?.find((child: IUsers) => child.id === props.selectedUser!.id);
    if (isNeedToChangeName) first_name = `${elem.emailOrRole} (${elem.children?.length ? elem.children.length - 1 : 0})`;
    else first_name = elem.first_name!;
    return {
      ...elem,
      first_name,
      children: elem.children ? elem.children.filter((user) => user.id !== props.selectedUser!.id) : [],
    };
  });
  
  dispatch(
    setUserAndData({
      dataUserTable: tableDataWithoutSelectedUser,
      users: users.filter((elem) => elem.id !== props.selectedUser!.id),
    })
  );

  const available_users = dataRoom?.available_users?.filter((user) => user.id !== props.selectedUser?.id) || [];
  const currentRoom = available_rooms.find(room => room.id === dataRoom?.id);
  const roomMinusMember = {
    ...currentRoom!,
    watermark_enabled: dataRoom?.watermark_enabled || false,
    available_users,
    rooms_constraints: {...currentRoom!.rooms_constraints, members_amount: currentRoom!.rooms_constraints.members_amount! - 1}
  };

  dispatch(setDataRoom(roomMinusMember));
  dispatch(setRooms(available_rooms.map((room) =>
    room.id === currentRoom?.id
      ? roomMinusMember
      : room
  )));
});

export const deleteUserHandle = createAsyncThunk('deleteUser/Users',
  async (props: { t: TFunction }, thunkApi) => {
  const state = thunkApi.getState() as RootState;
  const { dispatch } = thunkApi;
  const { messageApi } = state.windowState;
  const { dataRoom } = state.dataRoom;
  const { userWithIdProcessed, selectedUser } = state.dataUsers;

  dispatch(setProcessedUsers([...userWithIdProcessed, selectedUser?.id!]));
  dispatch(setIsConfirmDeleteOpen(false));
  
  try {
    await api.deleteSelectedUser(selectedUser!.id, dataRoom!.id);
    dispatch(deleteUserFromStates({selectedUser: selectedUser!}))

    messageApi?.success(props.t('Users.confirm.userExcludeSuccess'));
  } catch (e) {
    messageApi?.error(props.t('Users.confirm.userExcludeError'));
  } finally {
    dispatch(setProcessedUsers(userWithIdProcessed.filter(id => id !== selectedUser?.id)));
    dispatch(setUserChoosen(null))
  }
});


export const changeBlockStatusInStates = createAsyncThunk('changeBlockStatusInStates/Users',
  async (props: { selectedUser: IUsers }, thunkApi) => {
  const state = thunkApi.getState() as RootState;
  const { dataUserTable, users } = state.dataUsers;

  const { dispatch } = thunkApi;
  const tableDataWithNewStatusUser = dataUserTable.map((elem) => {
    return {
      ...elem,
      children: elem.children?.map((elem) => (
        elem.id === props.selectedUser!.id
          ? {...elem, is_blocked: !props.selectedUser?.is_blocked}
          : elem
        )),
      };
    });    

    dispatch(
      setUserAndData({
        dataUserTable: tableDataWithNewStatusUser,
        users: users.map((elem) => (
          elem.id === props.selectedUser!.id
            ? {...elem, is_blocked: !props.selectedUser?.is_blocked}
            : elem
        )),
      })
    );
  }
);

export const changeBlockStatus = createAsyncThunk('changeBlockStatus/Users',
  async (props: { t: TFunction }, thunkApi) => {
  const state = thunkApi.getState() as RootState;
  const { dispatch } = thunkApi;
  const { messageApi } = state.windowState;
  const { dataRoom } = state.dataRoom;
  const { userWithIdProcessed, selectedUser } = state.dataUsers;

  dispatch(setProcessedUsers([...userWithIdProcessed, selectedUser?.id!]));
  dispatch(setIsConfirmBlockOpen(false));

  try {
      await api.changeBlockStatus({
        user_ids: [selectedUser!.id],
        room_id: dataRoom!.id,
        is_blocked: !selectedUser?.is_blocked
      });
      dispatch(changeBlockStatusInStates({selectedUser: selectedUser!}))

      selectedUser?.is_blocked
        ? messageApi?.success(props.t('Users.confirm.userUnblockSuccess'))
        : messageApi?.success(props.t('Users.confirm.userBlockSuccess'));
    } catch (e) {
      selectedUser?.is_blocked
        ? messageApi?.error(props.t('Users.confirm.userUnblockError'))
        : messageApi?.error(props.t('Users.confirm.userBlockError'));
    } finally {
      dispatch(setProcessedUsers(userWithIdProcessed.filter(id => id !== selectedUser?.id)));
      dispatch(setUserChoosen(null));
    }
  }
);

export const deleteInvite = createAsyncThunk('deleteInvite/Users',
  async (props: { invite_id: string, t: TFunction }, thunkApi) => {
    const state = thunkApi.getState() as RootState;
    const { dispatch } = thunkApi;
    const { messageApi } = state.windowState;
    const { pendingInviteUsers } = state.dataUsers;

    try {
      await api.deleteInvite(props.invite_id);
      const filteredUsers = pendingInviteUsers.filter(({ id }) => id !== props.invite_id);
      dispatch(setPendingInviteUsers(filteredUsers.filter(el => el !== undefined)));
      dispatch(setDeletedInviteId(props.invite_id));
    } catch (e) {
      messageApi?.error(props.t('Users.modals.invite.failedDeleteInvite'));
    } finally {
    }
  }
);

export const reInviteHandle = createAsyncThunk('reInvite/Users',
  async (props: { currentUser: IPendingInviteUsers, t: TFunction }, thunkApi) => {
    const state = thunkApi.getState() as RootState;
    const { dispatch } = thunkApi;
    const { messageApi } = state.windowState;
    const { pendingInviteUsers } = state.dataUsers;    

    try {
      await api.reSendInvite({invite_id: props.currentUser.id});
      const newPendingUsers = pendingInviteUsers.map((user) =>
        user.id === props.currentUser.id ? { ...user, invite_status: 0 } : user
      );
      dispatch(setPendingInviteUsers(newPendingUsers.filter(el => el !== undefined)));
    } catch (e) {
      messageApi?.error(props.t('Users.modals.invite.failedReinvite'));
    } finally {
    }
  }
);

export default fetchGroupsAndUsers;
